import { message, notification } from 'antd'
import { Model } from 'dva'
import { OrganisationsService } from '../../Service'
import { FailureHandler } from 'Utils/ApiHelper'

const GlobalTriggers: Model = {
  namespace: 'globalTriggers',
  state: {
    list: [],
    search: '',
    selectedGlobalTrigger: null,
    setGlobalTriggerLoading: false,
    loading: true,
    editCurrentTab: 'general',
  },
  effects: {
    *list(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {} },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsPathsList, {
          organisationUuid,
          flowUuid,
          flowConfigVersion,
          displayInGlobalList: [true],
          fields: [
            'id',
            'destination_block',
            'user_input_example',
            'trigger',
            'intent',
            'name',
            'retries',
            'transition_type',
          ],
        })
        yield put({
          type: 'saveGlobalTriggersList',
          payload: response,
        })
        onSuccess(response)
      } catch (e) {
        FailureHandler('Failed To Fetch Global Triggers', e, onFailure)
      }
      setLoading(false)
    },
    *addGlobalTrigger({ payload, callback }, { call, put, select }): any {
      yield put({ type: 'toggleLoading', payload: true })
      const flowLayout = yield select((state: any) => state.flowLayout)
      let response
      try {
        // response = yield call(addGlobalTrigger, payload, true);
        response = yield call(OrganisationsService.organisationsFlowsVersionsPathsCreate, {
          organisationUuid: flowLayout.flow?.organisation?.uuid,
          flowUuid: flowLayout.flow.uuid,
          flowConfigVersion: flowLayout.version.version,
          requestBody: {
            ...payload,
            display_in_global_list: true,
          },
        })
        message.success('Added Trigger!')
        yield put({
          type: 'addGlobalTriggerToList',
          payload: response,
        })
      } catch (e: any) {
        FailureHandler('Failed To Add Global Trigger', e)
        yield put({ type: 'toggleLoading', payload: false })
        return
      }
      yield put({ type: 'toggleLoading', payload: false })
      if (callback) callback(response)
    },
    *partialUpdate(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {}, payload, pathId },
      { call, put, select }
    ): any {
      setLoading(true)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
      try {
        const response = yield call(
          OrganisationsService.organisationsFlowsVersionsPathsPartialUpdate,
          {
            organisationUuid,
            flowUuid,
            flowConfigVersion,
            id: pathId,
            requestBody: payload,
          }
        )
        onSuccess(response)
        yield put({ type: 'updateSelectedGlobalTriggerAndList', payload: response })
      } catch (e: any) {
        FailureHandler(
          'Error updating the Global Trigger.',
          `${e.response.status} | ${JSON.stringify(e.response)}`,
          onFailure
        )
      }
      setLoading(false)
    },
    *destroy({ onSuccess = () => {}, onFailure = () => {}, payload }, { call, put, select }): any {
      if (payload.type === 'Start') {
        message.error('Cannot delete the Start globalTrigger!')
        return
      }
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)

      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsPathsDestroy, {
          organisationUuid,
          flowUuid,
          flowConfigVersion,
          id: payload,
        })
        onSuccess(response)
      } catch (e: any) {
        FailureHandler('Error deleting the globalTrigger.', e, onFailure)
      }
      yield put({ type: 'removeGlobalTriggersFromList', payload: payload })
    },
    *retrieve(
      { setLoading = () => {}, onSuccess = () => {}, onFailure = () => {}, payload },
      { call, put, select }
    ): any {
      setLoading(false)
      const organisationUuid = yield select(
        (state: any) => state.flowLayout.flow?.organisation?.uuid
      )
      const flowUuid = yield select((state: any) => state.flowLayout.flow.uuid)
      const flowConfigVersion = yield select((state: any) => state.flowLayout.version.version)
      try {
        const response = yield call(OrganisationsService.organisationsFlowsVersionsPathsRetrieve, {
          organisationUuid,
          flowUuid,
          flowConfigVersion,
          id: payload,
        })
        yield put({ type: 'setSelectedGlobalTrigger', payload: response })
        onSuccess(response)
      } catch (e: any) {
        FailureHandler(
          'Error fetching globalTrigger.',
          `${e.response.status} | ${e.response}`,
          onFailure
        )
      }
      setLoading(true)
    },
  },
  reducers: {
    clearSelectedGlobalTrigger(state, action) {
      return {
        ...state,
        selectedGlobalTrigger: null,
      }
    },
    toggleLoading(state, action) {
      return {
        ...state,
        loading: action.payload,
      }
    },
    toggleSetGlobalTriggerLoading(state, action) {
      return {
        ...state,
        setGlobalTriggerLoading: action.payload,
      }
    },
    saveGlobalTriggersList(state, action) {
      return {
        ...state,
        list: action.payload,
      }
    },
    setSelectedGlobalTrigger(state, action) {
      return {
        ...state,
        selectedGlobalTrigger: action.payload,
      }
    },
    updateSelectedGlobalTrigger(state, action) {
      return {
        ...state,
        selectedGlobalTrigger: {
          ...state.selectedGlobalTrigger,
          ...action.payload,
        },
      }
    },
    updateSelectedGlobalTriggerAndList(state, action) {
      return {
        ...state,
        selectedGlobalTrigger:
          String(state.selectedGlobalTrigger?.id) === String(action.payload.id)
            ? action.payload
            : state.selectedGlobalTrigger,
        list: state.list.map((globalTrigger: any) => {
          if (String(action.payload?.id) === String(globalTrigger.id)) {
            return action.payload
          }
          return globalTrigger
        }),
      }
    },
    addGlobalTriggerToList(state, action) {
      return {
        ...state,
        list: [...state.list, action.payload],
      }
    },
    removeGlobalTriggersFromList(state, action) {
      return {
        ...state,
        list: state.list.filter(
          (globalTrigger: any) => String(globalTrigger.id) !== String(action.payload)
        ),
      }
    },

    setEditCurrentTab(state, action) {
      return {
        ...state,
        editCurrentTab: action.payload,
      }
    },

    removeBlockGlobalTriggersFromList(state, action) {
      return {
        ...state,
        list: state.list.filter((globalTrigger: any) => {
          if (String(globalTrigger.source_block?.id) === String(action.payload)) {
            return false
          }
          if (String(globalTrigger.destination_block?.id) === String(action.payload)) {
            return false
          }
          return true
        }),
      }
    },
    setSearch(state, action) {
      return {
        ...state,
        search: action.payload,
      }
    },
    resetGlobalTriggers(state, action) {
      return {
        ...state,
        list: [],
        search: '',
        selectedGlobalTrigger: null,
        setGlobalTriggerLoading: false,
        loading: true,
        editCurrentTab: 'general',
      }
    },
  },
}

export default GlobalTriggers
